import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  FormControlLabel,
  MenuItem,
  Select,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { DatePicker } from '@mui/x-date-pickers-pro';
import moment from 'moment';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import ApplicationList from './applications/ApplicationList';
import WalletList from './wallets/WalletList';
import SaveButtonComponent from '../../components/SaveButton';
import InfoIcon from '@mui/icons-material/Info';
import ToggleComponent from '../../components/ToggleField';
import { BreadcrumbsTitleContext } from '../../../wrappers/AppWrapper';
import { QueryStats } from '@mui/icons-material';
import { sgrColors } from '../../../ui/sgr/SgrColors';
import { ApiCustomer } from '../../../api/hexa/ApiCustomer';
import { ApiHexaBase } from '../../../api/hexa/ApiHexaBase';
import {
  customerDefaultFormValues,
  Customer,
} from '../../../api/hexa/models/CustomerModels';
import { HexaInterestArea } from '../../../api/hexa/models/SharedModels';
import InterestAreasSelect from '../../components/InterestAreasSelect';

const CustomerDetail = () => {
  const isResetting = useRef(false);
  const [initialFormValue, setInitialFormValue] = useState(
    customerDefaultFormValues
  );
  const [isFormUnchanged, setIsFormUnchanged] = useState(true);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [customer, setCustomer] = useState<Customer>();
  const [error, setError] = useState({
    open: false,
    message: '',
  });
  const { customerId } = useParams();
  const { setCustomerName } = useContext(BreadcrumbsTitleContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    control,
    register,
    watch,
    reset,
    getValues,
    trigger,
    formState: { isValid, errors },
  } = useForm<Customer>({
    defaultValues: customerDefaultFormValues,
    mode: 'onChange',
  });

  const flatSwitchValue = watch('contract.flat');
  const batchScreeningQuota = watch('contract.batch_screening_quota');
  const screeningQuota = watch('contract.screening_quota');
  const monitoringQuota = watch('contract.monitoring_quota');
  const formValues = useWatch({ control });

  /**
   * Fetch customer from the API and update the form with the fetched data
   */
  const fetchCustomer = useCallback(async () => {
    if (customerId === undefined || customerId === 'new') return;
    try {
      setLoading(true);
      let customer = await ApiCustomer.show(customerId);
      let customerName = customer?.name ?? `Customer ${customer.id}`;
      setCustomerName(customerName);
      localStorage.setItem('customerName', customerName);
      const customerFormValues = getCustomerFilledForm(customer);
      setCustomer(customer);
      isResetting.current = true;
      reset(customerFormValues);
      setInitialFormValue(customerFormValues);
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} fetching customer: 
        ${error.response?.data.error ?? 'Error fetching customer'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [customerId, reset, setCustomerName]);

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer, trigger]);

  /**
   * Check if the form values have changed, used to enable/disable save button
   */
  useEffect(() => {
    setIsFormUnchanged(
      JSON.stringify(formValues) === JSON.stringify(initialFormValue)
    );
    if (isResetting.current) {
      trigger();
      isResetting.current = false;
    }
  }, [formValues, initialFormValue, trigger]);

  /**
   * Submit the customer form data to the API, if triggered from the creation page
   * navigate to the detail of new custore, otherwise it updates the existing one
   * and updates the form with the new values
   */
  const onSubmit = useCallback(async () => {
    if (customerId === undefined) return;
    setLoading(true);
    const data: Partial<Customer> = getValues();
    try {
      if (customerId === 'new') {
        const res = await ApiCustomer.create(data);
        if (res.id) {
          navigate(`../${res.id}`);
        }
      } else {
        await ApiCustomer.update(customerId, data);
      }
      setInitialFormValue(data);
      reset(data);
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} updating or creating the customer: 
        ${error.response?.data.error ?? 'Error updating or creating the customer'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [customerId, getValues, navigate, reset]);

  /**
   * Close the error snackbar
   */
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setError({
      open: false,
      message: error.message,
    });
  };

  /**
   * Repeated part of template used when the flat switch is enabled
   */
  const noLimitRequests = flatSwitchValue && (
    <Typography variant="subtitle2" gutterBottom>
      {t('hexa_api.contract.no_limit')}
    </Typography>
  );

  return (
    <>
      <Container>
        <Card
          component="form"
          sx={{
            borderRadius: '0.5rem !important',
            paddingX: '1rem',
          }}
        >
          <CardHeader
            title={<Typography variant="h3">{t('customer_detail')}</Typography>}
          />
          <Stack
            direction="row"
            pb={'0.5rem'}
            pr={'1.5rem'}
            justifyContent="flex-end"
          >
            <SaveButtonComponent
              isLoading={isLoading}
              isFormUnchanged={isFormUnchanged}
              isValid={isValid}
              onSubmit={onSubmit}
            ></SaveButtonComponent>
          </Stack>
          <CardContent>
            <Stack direction="column" spacing={1}>
              <Grid2
                container
                spacing={2}
                columns={{ xs: 1, md: 2 }}
                alignItems="flex-start"
                sx={{
                  '& > .MuiGrid2-root': {
                    borderRadius: '0.25rem',
                    transition: 'background-color 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: sgrColors.hover_sky_blue,
                    },
                  },
                }}
              >
                <Grid2 xs={1}>
                  <Typography variant="body1" component="div">
                    {t('hexa_api.fields.name')}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  <TextField
                    size="small"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    {...register('name', {
                      required: t('hexa_api.fields.name_required'),
                    })}
                  />
                </Grid2>
                <Grid2 xs={1}>
                  <Typography variant="body1" component="div">
                    {t('hexa_api.fields.id')}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={t('hexa_api.fields.id_placeholder')}
                    InputProps={{
                      readOnly: true,
                    }}
                    {...register('id')}
                  />
                </Grid2>
                <Grid2
                  xs={1}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.2rem',
                  }}
                >
                  <Typography variant="body1" component="div">
                    {t('hexa_api.fields.odoo_id.title')}
                  </Typography>
                  <Tooltip title={t('hexa_api.fields.odoo_id.info')}>
                    <InfoIcon
                      sx={{ width: '1rem', height: '1rem' }}
                      fontSize="small"
                      color="info"
                    />
                  </Tooltip>
                </Grid2>
                <Grid2 xs={1}>
                  <TextField
                    size="small"
                    fullWidth
                    error={!!errors.odoo_id}
                    helperText={errors.odoo_id?.message}
                    {...register('odoo_id', {
                      required: t('hexa_api.fields.odoo_id.required'),
                    })}
                  />
                </Grid2>
                <ToggleComponent<Customer>
                  label="hexa_api.fields.active"
                  controlGroup={control}
                  controlName="active"
                />
                <ToggleComponent<Customer>
                  label="hexa_api.fields.api"
                  controlGroup={control}
                  controlName="api"
                />
                <ToggleComponent<Customer>
                  label="hexa_api.fields.business_base"
                  controlGroup={control}
                  controlName="business_base"
                />
                <ToggleComponent<Customer>
                  label="hexa_api.fields.dop"
                  controlGroup={control}
                  controlName="fdr"
                />
                <ToggleComponent<Customer>
                  label="hexa_api.fields.infocamere"
                  controlGroup={control}
                  controlName="infocamere"
                />
                <ToggleComponent<Customer>
                  label="hexa_api.fields.entity_events"
                  controlGroup={control}
                  controlName="entity_events"
                />
                <Controller
                  control={control}
                  name="interest_areas"
                  render={({ field: { onChange, value } }) => (
                    <InterestAreasSelect
                      value={value ?? []}
                      onChange={onChange}
                    />
                  )}
                />
                <ToggleComponent<Customer>
                  label="hexa_api.fields.live_screening"
                  controlGroup={control}
                  controlName="live_screening"
                />
                <ToggleComponent<Customer>
                  label="hexa_api.fields.live_dop"
                  controlGroup={control}
                  controlName="live_dop"
                />
                <ToggleComponent<Customer>
                  label="hexa_api.fields.translations"
                  controlGroup={control}
                  controlName="translations_enabled"
                />
                <ToggleComponent<Customer>
                  label="hexa_api.fields.recap"
                  controlGroup={control}
                  controlName="recap"
                />
                <Grid2 xs={1} sx={{ display: 'flex' }}>
                  <Typography variant="body1" sx={{ color: sgrColors.rose, fontWeight: 'bold' }}>
                    {t('hexa_api.fields.search_full_evidences')}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  <Controller
                    name="search_full_evidences"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        sx={{ m: 0 }}
                        labelPlacement="start"
                        control={
                          <Switch size="small" checked={!!value} onChange={onChange} />
                        }
                        label={
                          value
                            ? t('hexa_api.fields.active_state')
                            : t('hexa_api.fields.non_active_state')
                        }
                      />
                    )}
                  />
                </Grid2>
                <Grid2 xs={1} sx={{ display: 'flex' }}>
                  <Typography variant="body1" sx={{ color: sgrColors.rose, fontWeight: 'bold' }}>
                    {t('hexa_api.fields.search_full_associated_entities')}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  <Controller
                    name="search_full_associated_entities"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        sx={{ m: 0 }}
                        labelPlacement="start"
                        control={
                          <Switch size="small" checked={!!value} onChange={onChange} />
                        }
                        label={
                          value
                            ? t('hexa_api.fields.active_state')
                            : t('hexa_api.fields.non_active_state')
                        }
                      />
                    )}
                  />
                </Grid2>
                <Grid2
                  sx={{
                    paddingY: '0.8rem',
                    paddingX: '0',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                  xs={2}
                >
                  <Typography sx={{ fontWeight: '500' }} variant="h4">
                    {t('hexa_api.contract.title')}
                  </Typography>
                  <Button
                    disabled={customerId === 'new'}
                    onClick={() => navigate(`statistics`)}
                    color="primary"
                    aria-label="download"
                    variant="text"
                    startIcon={<QueryStats />}
                    sx={{
                      paddingLeft: '1rem',
                      '&:hover': {
                        fontColor: sgrColors.azure,
                        color: sgrColors.azure,
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {t('customer_statistics.title')}
                  </Button>
                </Grid2>
                <Grid2 xs={1}>
                  <Typography variant="body1">
                    {t('hexa_api.contract.start_date')}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  <Controller
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        slotProps={{
                          textField: { size: 'small', fullWidth: true },
                        }}
                        value={moment(value)}
                        onChange={onChange}
                      />
                    )}
                    control={control}
                    name="contract.start_date"
                  />
                </Grid2>
                <Grid2 xs={1}>
                  <Typography variant="body1">
                    {t('hexa_api.contract.end_date')}
                  </Typography>
                </Grid2>

                <Grid2 xs={1}>
                  <Controller
                    render={({ field: { onChange, value } }) => (
                      <>
                        <DatePicker
                          slotProps={{
                            textField: { size: 'small', fullWidth: true },
                          }}
                          value={moment(value)}
                          onChange={onChange}
                          maxDate={moment().add(1000, 'year')}
                        />
                      </>
                    )}
                    control={control}
                    name="contract.end_date"
                  />
                </Grid2>
                <Grid2 xs={1}>
                  <Typography variant="body1">
                    {t('hexa_api.contract.screening_quota')}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  <TextField
                    disabled={flatSwitchValue}
                    size="small"
                    fullWidth
                    type="number"
                    {...register('contract.screening_quota')}
                  />
                  {!flatSwitchValue && (
                    <Typography variant="subtitle2" gutterBottom>
                      {t('hexa_api.fields.remaining_requests')}
                      {screeningQuota -
                        (customer?.contract?.total_screening_used ?? 0)}
                    </Typography>
                  )}
                  {noLimitRequests}
                </Grid2>
                <Grid2 xs={1}>
                  <Typography variant="body1">
                    {t('hexa_api.contract.monitoring_quota')}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  <TextField
                    disabled={flatSwitchValue}
                    size="small"
                    fullWidth
                    type="number"
                    {...register('contract.monitoring_quota')}
                  />
                  {!flatSwitchValue && (
                    <Typography variant="subtitle2" gutterBottom>
                      {t('hexa_api.fields.remaining_requests')}{' '}
                      {monitoringQuota -
                        (customer?.contract?.total_monitoring_used ?? 0)}
                    </Typography>
                  )}
                  {noLimitRequests}
                </Grid2>
                <Grid2 xs={1}>
                  <Typography variant="body1">
                    {t('hexa_api.contract.batch_screening_quota')}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  <TextField
                    disabled={flatSwitchValue}
                    size="small"
                    fullWidth
                    type="number"
                    {...register('contract.batch_screening_quota')}
                  />
                  {!flatSwitchValue && (
                    <Typography variant="subtitle2" gutterBottom>
                      {t('hexa_api.fields.remaining_requests')}{' '}
                      {batchScreeningQuota -
                        (customer?.contract?.total_batch_screening_used ?? 0)}
                    </Typography>
                  )}
                  {noLimitRequests}
                </Grid2>
                <ToggleComponent<Customer>
                  label="hexa_api.contract.flat"
                  controlGroup={control}
                  controlName="contract.flat"
                />
                <Grid2 xs={1}>
                  <Typography variant="body1">
                    {t('hexa_api.contract.notes')}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    {...register('contract.notes')}
                  />
                </Grid2>
              </Grid2>
              {customerId && customerId !== 'new' && (
                <WalletList customerId={customerId} />
              )}
              {customerId && customerId !== 'new' && (
                <ApplicationList customerId={customerId} />
              )}
            </Stack>
          </CardContent>
        </Card>
        <Outlet />
      </Container>
      <Snackbar
        open={error.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error">
          {error.message}
        </Alert>
      </Snackbar>
    </>
  );
};

function getCustomerFilledForm(customer: Customer) {
  return {
    name: customer?.name ?? '',
    id: customer?.id.toString() ?? '',
    odoo_id: customer?.odoo_id ?? '',
    active: customer?.active ?? false,
    interest_areas: customer?.interest_areas ?? [],
    api: customer?.api ?? false,
    business_base: customer?.business_base ?? false,
    fdr: customer?.fdr ?? false,
    infocamere: customer?.infocamere ?? false,
    entity_events: customer?.entity_events ?? false,
    translations_enabled: customer?.translations_enabled ?? false,
    live_screening: customer?.live_screening ?? false,
    live_dop: customer?.live_dop ?? false,
    recap: customer?.recap ?? false,
    search_full_evidences: customer?.search_full_evidences ?? false,
    search_full_associated_entities: customer?.search_full_associated_entities ?? false,
    contract: {
      start_date: new Date(customer?.contract?.start_date ?? ''),
      end_date: new Date(customer?.contract?.end_date ?? ''),
      screening_quota: customer?.contract?.screening_quota ?? 0,
      monitoring_quota: customer?.contract?.monitoring_quota ?? 0,
      batch_screening_quota: customer?.contract?.batch_screening_quota ?? 0,
      flat: customer?.contract?.flat ?? false,
      notes: customer?.contract?.notes ?? '',
    },
  };
}

export default CustomerDetail;
